import { useGenerateDeepLink } from "@src/appV2/DeepLinks";
import { environmentConfig } from "@src/appV2/environment";
import { useToast } from "@src/appV2/lib";
import { APP_V2_APP_EVENTS, logError, logEvent } from "@src/appV2/lib/analytics";
import { share } from "@src/appV2/NativeAdapters/share.adapter";
import { type PlacementDetail } from "@src/appV2/redesign/Placements/api/useGetPlacement";
import { formatPayRange, formatPlacementShareTitle } from "@src/appV2/redesign/Placements/utils";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";

interface HandleShareJobParams {
  placementId: string;
  payRange: PlacementDetail["payRange"];
  workplaceName: string;
  qualificationDisplayLabel: string;
  formattedAddress: string;
}

export function useSharePlacementJob() {
  const { showErrorToast } = useToast();
  const worker = useDefinedWorker();
  const { mutateAsync: generateDeepLink, isLoading: isSharing } = useGenerateDeepLink();

  async function handleShareJob(params: HandleShareJobParams) {
    const { placementId, payRange, workplaceName, qualificationDisplayLabel, formattedAddress } =
      params;

    const eventContext = {
      placementId,
      workerId: worker.userId,
    };

    logEvent(APP_V2_APP_EVENTS.PLACEMENT_SHARE_JOB_CLICKED, eventContext);

    try {
      const deepLink = await generateDeepLink({
        link: `${environmentConfig.REACT_APP_WEB_APP_URL}/home-v2/placements/${placementId}`,
        skipPreview: true,
      });

      const payRangeText = formatPayRange({
        payRange,
        showPerHour: true,
        withFractionDigits: true,
      });

      const title = formatPlacementShareTitle({
        workplaceName,
        qualificationDisplayLabel,
        payRangeText,
        formattedAddress,
      });

      const payTextSegment = payRangeText ? ` - ${payRangeText}` : "";
      const text = `Check out this job on Clipboard: ${qualificationDisplayLabel} at ${workplaceName}${payTextSegment}. ${formattedAddress}\n\n${deepLink}`;

      try {
        await share({ title, text });
      } catch (shareError: unknown) {
        // Suppress user cancellation errors:
        // - Capacitor Share plugin throws "USER_CANCELLED" on Android
        // - Web Share API throws DOMException with name "AbortError"
        const isUserCancelled =
          (shareError instanceof Error && shareError.message === "USER_CANCELLED") ||
          (shareError instanceof DOMException && shareError.name === "AbortError");

        if (isUserCancelled) {
          return;
        }

        throw shareError;
      }

      logEvent(APP_V2_APP_EVENTS.PLACEMENT_SHARE_JOB_SUCCESS, eventContext);
    } catch (error) {
      logError(APP_V2_APP_EVENTS.PLACEMENT_SHARE_JOB_FAILURE, {
        metadata: eventContext,
        error,
      });
      showErrorToast("Failed to share job, please try again.");
    }
  }

  return { handleShareJob, isSharing };
}
